import { useCallback, useEffect, useRef, useState } from 'react';
import type { TranslationKeys } from '../../i18n/types.js';
import type { StudioCreateRequest } from './useStudioCommands.js';

/**
 * One map operation of a beat that is not confirmed yet. Nothing here is shown
 * as saved: the beat's maps are the server's (serverState.beats).
 */
export interface MapOp {
  readonly key: string;
  readonly kind: 'add' | 'remove' | 'primary' | 'move' | 'create';
  readonly beatId: string;
  /** The map the operation is about (for 'primary' — the new primary, '' = none; for 'create' — '' until made). */
  readonly pageId: string;
  /** 'create': the name the GM gave the new map. */
  readonly name?: string;
  /**
   * pending — sent; failed — refused («Retry» unless final); offline — answer
   * lost, re-sent after the rejoin (add / remove); lost — answer lost, NOT
   * re-sent (primary / order): the screen shows the server's state.
   * 'create' only: unknown — answer lost, the server is asked after the rejoin
   * what the request made (never a second map); unresolved — the server could
   * not say (no record): the map may exist, the GM checks the list.
   */
  readonly status: 'pending' | 'failed' | 'offline' | 'lost' | 'unknown' | 'unresolved';
  readonly error?: string;
  /** A refusal a retry would get again (the map is gone / not in the beat): no «Retry». */
  readonly final?: boolean;
}

interface Deps {
  readonly create: (request: StudioCreateRequest) => void;
  readonly campaignId: string;
  readonly t: (key: keyof TranslationKeys) => string;
  /**
   * The beat is still in the plan. The GM starts a new map from a beat that is
   * on screen, so «absent» here means deleted: the map stays in the campaign,
   * it is not added to a beat that is gone.
   */
  readonly beatExists: (beatId: string) => boolean;
}

/** page:create refusals a retry would get again: the campaign is full, the name is invalid. */
const CREATE_FINAL_CODES: readonly string[] = ['PAGE_LIMIT', 'INVALID_NAME'];

/** command:error codes a retry would get again. */
const FINAL_CODES: readonly string[] = ['map_not_found', 'map_not_in_beat', 'not_found', 'wrong_campaign'];

/**
 * A beat's maps (GM Studio 6): add / remove one map, choose the primary,
 * move a map. Each operation is one server command correlated by requestId
 * (useStudioCommands); the list on screen changes only by the server's
 * beat:maps-changed. add / remove are idempotent on the server and re-sent
 * after a lost answer; the primary and the order are not — a stale choice
 * must not override another GM's newer one, the rejoin snapshot is shown.
 * A second click while an operation of the same kind is in flight sends
 * nothing.
 *
 * «New map» from a beat (createAndAdd): page:create, then beat:add-map of the
 * map the server made, to the beat the GM started from — each on its own
 * answer. A lost create answer is asked about after the rejoin
 * (request:outcome), never re-sent: a second map would be a duplicate.
 */
export function useBeatMaps(deps: Deps): {
  readonly ops: readonly MapOp[];
  readonly add: (beatId: string, pageId: string, primaryIfNone: boolean) => void;
  readonly remove: (beatId: string, pageId: string, nextPrimaryPageId: string | null) => void;
  readonly setPrimary: (beatId: string, pageId: string | null) => void;
  readonly move: (beatId: string, pageId: string, beforePageId: string | null) => void;
  /** Make a new map and add it to the beat; onMapCreated runs once the server made it. */
  readonly createAndAdd: (beatId: string, name: string, primaryIfNone: boolean, onMapCreated?: (pageId: string) => void) => void;
  readonly retry: (key: string) => void;
  readonly dismiss: (key: string) => void;
  /** The beats are gone: nothing of theirs is sent or awaited any more. */
  readonly cancel: (beatIds: readonly string[]) => void;
} {
  const depsRef = useRef(deps);
  depsRef.current = deps;
  const opsRef = useRef<readonly MapOp[]>([]);
  const [ops, setOps] = useState<readonly MapOp[]>([]);
  const cancelledRef = useRef(new Set<string>());
  // What «Retry» of a failed operation sends again.
  const resendRef = useRef(new Map<string, () => void>());

  const put = useCallback((op: MapOp): void => {
    const next = [...opsRef.current.filter((o) => o.key !== op.key), op];
    opsRef.current = next;
    setOps(next);
  }, []);
  const drop = useCallback((key: string): void => {
    resendRef.current.delete(key);
    const next = opsRef.current.filter((o) => o.key !== key);
    if (next.length === opsRef.current.length) return;
    opsRef.current = next;
    setOps(next);
  }, []);
  const find = (key: string): MapOp | undefined => opsRef.current.find((o) => o.key === key);

  const send = useCallback(
    (
      op: Omit<MapOp, 'status' | 'error' | 'final'>,
      command: 'beat:add-map' | 'beat:remove-map' | 'beat:set-primary-map' | 'beat:move-map',
      payload: Record<string, unknown>,
      failKey: keyof TranslationKeys,
    ): void => {
      const { key, beatId } = op;
      if (cancelledRef.current.has(beatId)) return;
      const pending = find(key);
      if (pending !== undefined && pending.status === 'pending') return; // a double click sends once
      put({ ...op, status: 'pending' });
      const again = (): void => send(op, command, payload, failKey);
      resendRef.current.set(key, again);
      const resend = op.kind === 'add' || op.kind === 'remove';
      depsRef.current.create({
        command,
        echo: 'beat:maps-changed',
        name: '',
        failTitle: depsRef.current.t(failKey),
        payload: { campaignId: depsRef.current.campaignId, beatId, ...payload },
        matches: (echo, requestId) => (echo.requestId === requestId ? beatId : undefined),
        onCreated: (): void => drop(key),
        onFailed: (reason: string, final?: boolean): void => {
          if (find(key) !== undefined) put({ ...op, status: 'failed', error: reason, final: final === true });
        },
        finalCodes: FINAL_CODES,
        onUnknown: (): void => {
          if (find(key) !== undefined) put({ ...op, status: resend ? 'offline' : 'lost' });
        },
        // add / remove: idempotent, re-sent with the same requestId after the rejoin.
        ...(resend ? { recover: 'resend' as const } : {}),
        alive: (): boolean => !cancelledRef.current.has(beatId) && find(key)?.status === 'offline',
        retry: (): void => {
          if (find(key)?.status === 'failed') again();
        },
      });
    },
    [put, drop],
  );

  const add = useCallback(
    (beatId: string, pageId: string, primaryIfNone: boolean): void => {
      send({ key: `add ${beatId} ${pageId}`, kind: 'add', beatId, pageId }, 'beat:add-map',
        { pageId, ...(primaryIfNone ? { primaryIfNone: true } : {}) }, 'studio.maps.addFailed');
    },
    [send],
  );
  const remove = useCallback(
    (beatId: string, pageId: string, nextPrimaryPageId: string | null): void => {
      send({ key: `remove ${beatId} ${pageId}`, kind: 'remove', beatId, pageId }, 'beat:remove-map',
        { pageId, ...(nextPrimaryPageId !== null ? { nextPrimaryPageId } : {}) }, 'studio.maps.removeFailed');
    },
    [send],
  );
  // One primary / one order change per beat at a time.
  const setPrimary = useCallback(
    (beatId: string, pageId: string | null): void => {
      send({ key: `primary ${beatId}`, kind: 'primary', beatId, pageId: pageId ?? '' }, 'beat:set-primary-map',
        { pageId }, 'studio.maps.primaryFailed');
    },
    [send],
  );
  const move = useCallback(
    (beatId: string, pageId: string, beforePageId: string | null): void => {
      send({ key: `move ${beatId}`, kind: 'move', beatId, pageId }, 'beat:move-map',
        { pageId, beforePageId }, 'studio.maps.moveFailed');
    },
    [send],
  );

  const nextCreateRef = useRef(0);
  const startCreate = useCallback(
    (key: string, beatId: string, name: string, primaryIfNone: boolean, onMapCreated?: (pageId: string) => void): void => {
      if (cancelledRef.current.has(beatId)) return;
      const pending = find(key);
      if (pending !== undefined && (pending.status === 'pending' || pending.status === 'unknown')) return;
      const base = { key, kind: 'create' as const, beatId, pageId: '', name };
      put({ ...base, status: 'pending' });
      resendRef.current.set(key, (): void => startCreate(key, beatId, name, primaryIfNone, onMapCreated));
      depsRef.current.create({
        command: 'page:create',
        echo: 'page:created',
        name,
        failTitle: depsRef.current.t('studio.maps.createFailed'),
        payload: { name },
        matches: (echo, requestId) =>
          echo.requestId === requestId ? (echo.page as { id?: string } | undefined)?.id : undefined,
        onCreated: (pageId: string): void => {
          drop(key);
          // The map exists in the campaign either way; it joins the beat the GM
          // started from — if that beat is still there.
          if (cancelledRef.current.has(beatId) || !depsRef.current.beatExists(beatId)) return;
          add(beatId, pageId, primaryIfNone);
          onMapCreated?.(pageId);
        },
        onFailed: (reason: string, final?: boolean): void => {
          if (find(key) !== undefined) put({ ...base, status: 'failed', error: reason, final: final === true });
        },
        finalCodes: CREATE_FINAL_CODES,
        onUnknown: (): void => {
          if (find(key) !== undefined) put({ ...base, status: 'unknown' });
        },
        onUnresolved: (reason: string): void => {
          if (find(key) !== undefined) put({ ...base, status: 'unresolved', error: reason });
        },
        // After a lost answer the server is asked what this request made — never a second map.
        recover: 'ask',
        alive: (): boolean => !cancelledRef.current.has(beatId) && find(key) !== undefined,
        retry: (): void => {
          if (find(key)?.status === 'failed') startCreate(key, beatId, name, primaryIfNone, onMapCreated);
        },
      });
    },
    [put, drop, add],
  );
  const createAndAdd = useCallback(
    (beatId: string, name: string, primaryIfNone: boolean, onMapCreated?: (pageId: string) => void): void => {
      startCreate(`create ${beatId} ${++nextCreateRef.current}`, beatId, name, primaryIfNone, onMapCreated);
    },
    [startCreate],
  );

  // Inline «Retry»: only a refusal that a retry would not get again.
  const retry = useCallback((key: string): void => {
    const op = find(key);
    if (op === undefined || op.status !== 'failed' || op.final === true) return;
    resendRef.current.get(key)?.();
  }, []);

  const cancel = useCallback((beatIds: readonly string[]): void => {
    for (const beatId of beatIds) cancelledRef.current.add(beatId);
    const next = opsRef.current.filter((o) => !beatIds.includes(o.beatId));
    if (next.length !== opsRef.current.length) {
      opsRef.current = next;
      setOps(next);
    }
  }, []);

  // Another campaign: none of these operations.
  useEffect(() => {
    return (): void => {
      opsRef.current = [];
      setOps([]);
      cancelledRef.current.clear();
      resendRef.current.clear();
    };
  }, [deps.campaignId]);

  return { ops, add, remove, setPrimary, move, createAndAdd, retry, dismiss: drop, cancel };
}
